/**
 * Modelos selecionáveis no seletor da conversa, por motor (agente).
 *
 * `id` vazio ("") = usar o modelo default do motor (sem override). Os ids
 * Claude seguem os nomes aceitos pelo Claude Agent SDK; os ids Codex seguem
 * os `slug` da conta OpenAI logada (variam por conta/plano).
 */
export type AgentKind = "claude" | "codex" | "gemini";

export interface ModelOption {
  id: string;
  agent: AgentKind;
  label: string;
  hint: string;
}

export const GEMINI_DEFAULT_MODEL_ID = "gemini-default";

export const AGENT_LABEL: Record<AgentKind, string> = {
  claude: "Claude",
  codex: "Codex",
  gemini: "Gemini",
};

/**
 * Sentinela do "default" do Codex - não pode ser "" porque "" já é o
 * sentinela do default do Claude, e os dois precisam ser distinguíveis dentro
 * de `allowedModels` (lista plana, compartilhada entre os dois motores).
 */
export const CODEX_DEFAULT_MODEL_ID = "codex-default";

export const MODEL_OPTIONS: ModelOption[] = [
  { id: "", agent: "claude", label: "Padrão do servidor", hint: "Usa o modelo configurado no container" },
  { id: "claude-opus-4-8", agent: "claude", label: "Opus 4.8", hint: "Mais capaz — análises profundas" },
  { id: "claude-sonnet-4-6", agent: "claude", label: "Sonnet 4.6", hint: "Equilíbrio entre velocidade e qualidade" },
  { id: "claude-haiku-4-5", agent: "claude", label: "Haiku 4.5", hint: "Mais rápido e barato — perguntas simples" },
  { id: "claude-fable-5", agent: "claude", label: "Fable 5", hint: "Modelo adicional da família Claude 5" },
  { id: CODEX_DEFAULT_MODEL_ID, agent: "codex", label: "Padrão da conta", hint: "Usa o modelo default da conta Codex" },
  { id: "gpt-6-astra", agent: "codex", label: "GPT-6 Astra", hint: "Mais capaz — trabalho complexo" },
  { id: "gpt-5.6-terra", agent: "codex", label: "GPT-5.6 Terra", hint: "Equilíbrio entre velocidade e qualidade" },
  { id: "gpt-5.6-luna", agent: "codex", label: "GPT-5.6 Luna", hint: "Rápido e econômico" },
  { id: GEMINI_DEFAULT_MODEL_ID, agent: "gemini", label: "Padrão da conta", hint: "Usa o modelo default do Gemini CLI" },
  { id: "gemini-2.5-pro", agent: "gemini", label: "Gemini 2.5 Pro", hint: "Mais capaz — análises profundas" },
  { id: "gemini-2.5-flash", agent: "gemini", label: "Gemini 2.5 Flash", hint: "Equilíbrio entre velocidade e custo" },
  { id: "gemini-2.5-flash-lite", agent: "gemini", label: "Gemini 2.5 Flash Lite", hint: "Mais rápido e barato" },
];

export function modelsForAgent(agent: AgentKind): ModelOption[] {
  return MODEL_OPTIONS.filter((m) => m.agent === agent);
}

export function modelLabel(id: string | undefined, agent: AgentKind = "claude"): string {
  if (!id) return "Padrão";
  const found = MODEL_OPTIONS.find((m) => m.id === id && m.agent === agent) ?? MODEL_OPTIONS.find((m) => m.id === id);
  if (found) return found.label;
  // modelo custom (vindo de env): mostra forma curta
  return id.replace(/^claude-/, "").replace(/-\d{8}$/, "");
}
